import KanbanIcon from '@atlaskit/icon-lab/core/kanban';
import CalendarIcon from "@atlaskit/icon/core/calendar";
import CardIcon from "@atlaskit/icon/core/card";
import ChartBubbleIcon from "@atlaskit/icon/core/chart-bubble";
import ListChecklistIcon from "@atlaskit/icon/core/list-checklist";
import TargetIcon from "@atlaskit/icon/core/target";
import QuestionCircleIcon from "@atlaskit/icon/core/question-circle";
import TableIcon from '@atlaskit/icon/core/table';
import {token} from "@atlaskit/tokens";
import type {CSSProperties, ReactNode} from "react";
import {twJoin} from "tailwind-merge";
import type {OttopilotDataViewerController} from "~/components/ottopilotDataViewer/ottopilotDataViewerController";
import {sortOttopilotViewsForDisplayOrder, ViewType} from "~/components/ottopilotDataViewer/view";
import {argusDomAttributes, toArgusHtmlDataAttributes, type ArgusDomAttributes} from "~/workspaceConfig/argusDomAttributes";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {ItemBuilder} from "@vesta/global-common-typescript/components/itemBuilder";

function viewTypeArgusName(viewType: ViewType): string {
    switch (viewType) {
        case ViewType.list: {
            return "list";
        }
        case ViewType.table: {
            return "table";
        }
        case ViewType.focus: {
            return "focus";
        }
        case ViewType.kanbanBoard: {
            return "kanban";
        }
        case ViewType.calendar: {
            return "calendar";
        }
        case ViewType.cards: {
            return "cards";
        }
        case ViewType.graph: {
            return "graph";
        }
        default: {
            return "view";
        }
    }
}

function viewClickedArgusAttributes(viewType: ViewType): ArgusDomAttributes {
    return argusDomAttributes({
        event: "viewClicked",
        props: {
            view_name: viewTypeArgusName(viewType),
        },
    });
}

export function viewTabLabel(viewType: ViewType): string {
    switch (viewType) {
        case ViewType.list: {
            return "List";
        }
        case ViewType.table: {
            return "Table";
        }
        case ViewType.focus: {
            return "Focus";
        }
        case ViewType.kanbanBoard: {
            return "Kanban";
        }
        case ViewType.calendar: {
            return "Calendar";
        }
        case ViewType.cards: {
            return "Cards";
        }
        case ViewType.graph: {
            return "Chart";
        }
        default: {
            return "View";
        }
    }
}

export function viewTabIcon(viewType: ViewType): ReactNode {
    const iconProps = {label: "", size: "medium" as const};
    switch (viewType) {
        case ViewType.list: {
            return <ListChecklistIcon {...iconProps} />;
        }
        case ViewType.calendar: {
            return <CalendarIcon {...iconProps} />;
        }
        case ViewType.cards: {
            return <CardIcon {...iconProps} />;
        }
        case ViewType.graph: {
            return <ChartBubbleIcon {...iconProps} />;
        }
        case ViewType.kanbanBoard: {
            return <KanbanIcon {...iconProps} />;
        }
        case ViewType.table: {
            return <TableIcon {...iconProps} />;
        }
        case ViewType.focus: {
            return <TargetIcon {...iconProps} />;
        }
        default: {
            return <QuestionCircleIcon {...iconProps} />;
        }
    }
}

const drawerOptionTextStyle: CSSProperties = {
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 1,
    alignSelf: "stretch",
    overflow: "hidden",
};

export function ViewPickerDrawerContent<
    T extends {
        id: Uuid;
    },
>({
    controller,
    selectedViewId,
    onSelectedViewIdChange,
}: {
    controller: OttopilotDataViewerController<T>;
    selectedViewId: Uuid | null;
    onSelectedViewIdChange: (viewId: Uuid) => void;
}) {
    const orderedPickerViews = sortOttopilotViewsForDisplayOrder(controller.views);

    if (orderedPickerViews.length <= 1) {
        return null;
    }

    return (
        <div className="grid grid-cols-1 gap-ds-space-100">
            {orderedPickerViews.map((view) => {
                const isSelected = view.id === selectedViewId;
                const label = viewTabLabel(view.viewType);
                return (
                    <label
                        key={view.id}
                        className={twJoin(
                            "grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-ds-space-100 w-full min-h-[40px] px-ds-space-150 py-ds-space-100 rounded-[4px] cursor-pointer",
                            "bg-ds-background-input hover:bg-ds-background-neutral-hovered",
                        )}
                        {...toArgusHtmlDataAttributes(viewClickedArgusAttributes(view.viewType))}
                    >
                        <input
                            type="radio"
                            name="data-view-picker"
                            checked={isSelected}
                            onChange={() => {
                                onSelectedViewIdChange(view.id);
                            }}
                            className="w-4 h-4 shrink-0 accent-ds-icon-brand cursor-pointer"
                        />
                        <span className="text-sm font-medium text-ds-text" style={drawerOptionTextStyle}>
                            {label}
                        </span>
                    </label>
                );
            })}
        </div>
    );
}

export function ViewPicker<
    T extends {
        id: Uuid;
    },
>({controller, className}: {controller: OttopilotDataViewerController<T>; className?: string}) {
    const orderedPickerViews = sortOttopilotViewsForDisplayOrder(controller.views);

    if (orderedPickerViews.length <= 1) {
        return null;
    }

    return (
        <div
            className={twJoin("w-full border-b border-solid border-ds-border", className)}
            role="tablist"
            aria-label="Data view"
        >
            <div className="grid w-max max-w-full grid-flow-col auto-cols-max items-end justify-start gap-x-ds-space-50">
                <ItemBuilder
                    items={orderedPickerViews}
                    itemBuilder={(view) => {
                        const isSelected = view.id === controller.currentViewId;
                        const label = viewTabLabel(view.viewType);
                        return (
                            <button
                                key={view.id}
                                type="button"
                                role="tab"
                                aria-selected={isSelected}
                                aria-label={`${label} view`}
                                {...toArgusHtmlDataAttributes(viewClickedArgusAttributes(view.viewType))}
                                onClick={() => {
                                    controller.setExplicitViewId(view.id);
                                }}
                                className={twJoin(
                                    "grid grid-flow-col auto-cols-max items-center gap-x-ds-space-050 p-ds-space-100",
                                    "-mb-px border-b-[3px] border-solid bg-transparent cursor-pointer",
                                    "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ds-border-accent-blue",
                                    isSelected
                                        ? "text-ds-text-accent-blue"
                                        : "border-transparent text-ds-text hover:text-ds-text",
                                )}
                                style={{
                                    borderBottomColor: isSelected ? token("color.border.accent.blue") : "transparent",
                                }}
                            >
                                <span className="grid place-items-center shrink-0 text-current [&_svg]:text-current" aria-hidden>
                                    {viewTabIcon(view.viewType)}
                                </span>
                                <span className="text-sm font-semibold leading-none tracking-tight whitespace-nowrap">
                                    {label}
                                </span>
                            </button>
                        );
                    }}
                />
            </div>
        </div>
    );
}
